/**
 * Weather service supporting multiple providers
 * - National Weather Service (NWS) for US locations
 * - Open-Meteo for global coverage
 */

export interface WeatherData {
  current: {
    temperatureF: number;
    windMph: number;
    precipitation: boolean;
    feelsLikeF: number;
    humidity: number;
  };
  hourly: Array<{
    time: string;
    temperatureF: number;
    windMph: number;
    precipitation: boolean;
  }>;
  location: {
    name: string;
    lat: number;
    lon: number;
  };
}

export interface Location {
  type: 'zip' | 'coords' | 'city';
  value: string;
  lat?: number;
  lon?: number;
}

export class WeatherService {
  /**
   * Fetch weather data for a location
   */
  async getWeather(location: Location): Promise<WeatherData> {
    // Get coordinates
    const coords = await this.resolveCoordinates(location);
    
    // Try NWS first for US locations, fallback to Open-Meteo
    try {
      const isUS = this.isUSLocation(coords);
      if (isUS) {
        return await this.fetchNWS(coords);
      }
    } catch (error) {
      console.warn('NWS failed, falling back to Open-Meteo:', error);
    }
    
    return await this.fetchOpenMeteo(coords);
  }

  /**
   * Resolve location to coordinates
   */
  private async resolveCoordinates(location: Location): Promise<{ lat: number; lon: number }> {
    if (location.lat !== undefined && location.lon !== undefined) {
      return { lat: location.lat, lon: location.lon };
    }

    if (location.type === 'zip') {
      return await this.zipToCoords(location.value);
    }

    if (location.type === 'city') {
      return await this.cityToCoords(location.value);
    }

    throw new Error('Invalid location format');
  }

  /**
   * Convert ZIP code to coordinates using NWS API
   */
  private async zipToCoords(zip: string): Promise<{ lat: number; lon: number }> {
    // Use a geocoding service - for now using a simple approximation
    // In production, use a proper geocoding API
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?postalcode=${zip}&country=US&format=json`
    );
    const data = await response.json();
    
    if (data.length === 0) {
      throw new Error(`Could not find coordinates for ZIP code: ${zip}`);
    }

    return {
      lat: parseFloat(data[0].lat),
      lon: parseFloat(data[0].lon),
    };
  }

  /**
   * Convert city name to coordinates
   */
  private async cityToCoords(city: string): Promise<{ lat: number; lon: number }> {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(city)}&format=json&limit=1`
    );
    const data = await response.json();
    
    if (data.length === 0) {
      throw new Error(`Could not find coordinates for city: ${city}`);
    }

    return {
      lat: parseFloat(data[0].lat),
      lon: parseFloat(data[0].lon),
    };
  }

  /**
   * Check if coordinates are in US (rough approximation)
   */
  private isUSLocation(coords: { lat: number; lon: number }): boolean {
    return (
      coords.lat >= 24 && coords.lat <= 50 &&
      coords.lon >= -125 && coords.lon <= -65
    );
  }

  /**
   * Fetch from National Weather Service (US only)
   */
  private async fetchNWS(coords: { lat: number; lon: number }): Promise<WeatherData> {
    // Step 1: Get grid point info
    const pointResponse = await fetch(
      `https://api.weather.gov/points/${coords.lat.toFixed(4)},${coords.lon.toFixed(4)}`,
      {
        headers: {
          'User-Agent': 'BBQ-Calculator/1.0',
        },
      }
    );

    if (!pointResponse.ok) {
      throw new Error(`NWS API error: ${pointResponse.status}`);
    }

    const pointData = await pointResponse.json();
    const forecastHourlyUrl = pointData.properties.forecastHourly;
    const observationStationsUrl = pointData.properties.observationStations;

    // Step 2: Get current conditions
    const stationsResponse = await fetch(observationStationsUrl);
    const stationsData = await stationsResponse.json();
    const stationId = stationsData.features[0]?.id;

    let current = {
      temperatureF: 50,
      windMph: 5,
      precipitation: false,
      feelsLikeF: 50,
      humidity: 50,
    };

    if (stationId) {
      try {
        const obsResponse = await fetch(`${stationId}/observations/latest`);
        const obsData = await obsResponse.json();
        const props = obsData.properties;

        current = {
          temperatureF: this.celsiusToFahrenheit(props.temperature.value),
          windMph: this.mpsToMph(props.windSpeed.value),
          precipitation: props.precipitationLastHour?.value > 0,
          feelsLikeF: this.celsiusToFahrenheit(props.windChill?.value || props.temperature.value),
          humidity: props.relativeHumidity.value,
        };
      } catch (error) {
        console.warn('Failed to fetch current conditions:', error);
      }
    }

    // Step 3: Get hourly forecast
    const forecastResponse = await fetch(forecastHourlyUrl);
    const forecastData = await forecastResponse.json();

    const hourly = forecastData.properties.periods.slice(0, 48).map((period: any) => ({
      time: period.startTime,
      temperatureF: period.temperature,
      windMph: parseInt(period.windSpeed.split(' ')[0]) || 0,
      precipitation: period.probabilityOfPrecipitation?.value > 30,
    }));

    return {
      current,
      hourly,
      location: {
        name: pointData.properties.relativeLocation.properties.city + ', ' + 
              pointData.properties.relativeLocation.properties.state,
        lat: coords.lat,
        lon: coords.lon,
      },
    };
  }

  /**
   * Fetch from Open-Meteo (global coverage)
   */
  private async fetchOpenMeteo(coords: { lat: number; lon: number }): Promise<WeatherData> {
    const url = new URL('https://api.open-meteo.com/v1/forecast');
    url.searchParams.set('latitude', coords.lat.toString());
    url.searchParams.set('longitude', coords.lon.toString());
    url.searchParams.set('current', 'temperature_2m,relative_humidity_2m,apparent_temperature,precipitation,wind_speed_10m');
    url.searchParams.set('hourly', 'temperature_2m,precipitation,wind_speed_10m');
    url.searchParams.set('temperature_unit', 'fahrenheit');
    url.searchParams.set('wind_speed_unit', 'mph');
    url.searchParams.set('precipitation_unit', 'inch');
    url.searchParams.set('forecast_days', '3');

    const response = await fetch(url.toString());
    
    if (!response.ok) {
      throw new Error(`Open-Meteo API error: ${response.status}`);
    }

    const data = await response.json();

    const current = {
      temperatureF: data.current.temperature_2m,
      windMph: data.current.wind_speed_10m,
      precipitation: data.current.precipitation > 0,
      feelsLikeF: data.current.apparent_temperature,
      humidity: data.current.relative_humidity_2m,
    };

    const hourly = data.hourly.time.slice(0, 48).map((time: string, i: number) => ({
      time,
      temperatureF: data.hourly.temperature_2m[i],
      windMph: data.hourly.wind_speed_10m[i],
      precipitation: data.hourly.precipitation[i] > 0,
    }));

    return {
      current,
      hourly,
      location: {
        name: `${coords.lat.toFixed(2)}, ${coords.lon.toFixed(2)}`,
        lat: coords.lat,
        lon: coords.lon,
      },
    };
  }

  private celsiusToFahrenheit(celsius: number | null): number {
    if (celsius === null) return 50;
    return celsius * 9/5 + 32;
  }

  private mpsToMph(mps: number | null): number {
    if (mps === null) return 0;
    return mps * 2.237;
  }
}
